import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  MutationDefinition,
  QueryDefinition,
} from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { LazyQueryTrigger, MutationTrigger } from '@reduxjs/toolkit/src/query/react/buildHooks';
import store from '@/core/store/store';
import { COMMON_STORE_KEY } from '@/core/store/commonReducer';
import { dashboardApi } from '@/dashboard/services/dashboardApi';
import { RTKQueryDerivedBooleansType } from '@/components/QueryResponseLoader/QueryResponseLoader';
import coreTokens from '@/core/tokens.json';
import { getCurrentMtDateTimeString } from '@/common/utils/date.utils';

export type AppStandardErrorResponse = {
  exceptionMessage: string;
  context: string;
};
export type AppStandardResponse<T> = {
  data: {
    [key: string]: T[];
  };
  errors: [] | AppStandardErrorResponse[];
};
export type AppStandardResponseWithoutNesting<T> = {
  data: T;
  errors: [] | AppStandardErrorResponse[];
};
export type LazyGetTriggerType<T, U> = LazyQueryTrigger<
  QueryDefinition<
    T,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>,
    never,
    U,
    'api'
  >
>;

export type MutationTriggerType<T, U> = MutationTrigger<
  MutationDefinition<
    T,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>,
    never,
    U,
    'api'
  >
>;

type ApiErrorsCommonShape = {
  status: '400' | '500' | '200';
  detail: string;
  title: string;
  traceId: string;
};
type LegacyApiErrors = ApiErrorsCommonShape & {
  Errors: { ExceptionMessage: string; Context: string }[];
};
type EnhancedLegacyApiErrors = ApiErrorsCommonShape & {
  errors: { ExceptionMessage: string; Context: string }[];
};
type ModernApiErrors = ApiErrorsCommonShape & {
  errors: { exceptionMessage: string; context: string }[];
};
export type ApiErrorsShape = LegacyApiErrors | EnhancedLegacyApiErrors | ModernApiErrors;
const isLegacyApiError = (errors: ApiErrorsShape): errors is LegacyApiErrors =>
  'Errors' in errors && errors.Errors.length > 0 && 'ExceptionMessage' in errors.Errors[0];
const isEnhancedLegacyApiError = (errors: ApiErrorsShape): errors is EnhancedLegacyApiErrors =>
  'errors' in errors && errors.errors.length > 0 && 'ExceptionMessage' in errors.errors[0];
const isModernApiErrors = (errors: ApiErrorsShape): errors is ModernApiErrors =>
  'errors' in errors && errors.errors.length > 0 && 'exceptionMessage' in errors.errors[0];

export function transformApiErrorIntoMessages(response: FetchBaseQueryError) {
  const data = response.data as ApiErrorsShape;
  const traceId = data?.traceId;
  let transformed: string[] = [];

  if (!data) {
    return transformed;
  }

  try {
    if (isLegacyApiError(data)) {
      if (data.Errors && data.Errors.length > 0) {
        transformed = data.Errors.map((error) =>
          getErrorMessageWithTraceId(error.ExceptionMessage, traceId),
        );
      }
    } else if (isEnhancedLegacyApiError(data)) {
      if (data.errors && data.errors.length > 0) {
        transformed = data.errors.map((error) =>
          getErrorMessageWithTraceId(error.ExceptionMessage, traceId),
        );
      }
    } else if (isModernApiErrors(data)) {
      if (data.errors && data.errors.length > 0) {
        transformed = data.errors.map((error) =>
          getErrorMessageWithTraceId(error.exceptionMessage, traceId),
        );
      }
    }
  } catch (e) {
    return [];
  }

  return transformed.filter((error) => error && Boolean(error) && typeof error === 'string');
}

export function getFirstErrorOrDefault(
  response: FetchBaseQueryError,
  errorMessage = coreTokens['GENERAL_ERROR'],
) {
  const errors = transformApiErrorIntoMessages(response);
  if (errors.length > 0) {
    return errors[0];
  }

  const traceId = (response.data as ApiErrorsShape)?.traceId;
  return getErrorMessageWithTraceId(errorMessage, traceId);
}

export function getErrorMessageWithTraceId(errorMessage: string, traceId?: string) {
  if (traceId) {
    return `${errorMessage} (trace-id: ${traceId})`;
  }

  const currentDate = getCurrentMtDateTimeString();
  return typeof errorMessage === 'string'
    ? `${errorMessage} (timestamp: ${currentDate})`
    : errorMessage;
}

export function getCachedCountsOrExecuteNew(period: string) {
  let promise = null;
  const storeValue = store.getState();
  const countsFromStore = storeValue[COMMON_STORE_KEY].counts[period];

  if (countsFromStore && Object.keys(countsFromStore).length > 0) {
    promise = Promise.resolve({ [period]: countsFromStore });
  } else {
    const summarizePromise = store.dispatch(
      dashboardApi.endpoints.getCounts.initiate({ periods: [period] }),
    );

    promise = summarizePromise.unwrap();
  }

  return promise;
}

export function isQueryCacheExist(query: string, params: object) {
  const storeValue = store.getState();
  const queryCache = storeValue.api.queries[`${query}(${JSON.stringify(params)})`];
  if (queryCache?.status === 'fulfilled') {
    return true;
  }
  return false;
}

export function getRangeFromHeader(meta: FetchBaseQueryMeta | undefined) {
  const contentRange = meta?.response?.headers.get('Content-Range');

  if (!contentRange) {
    throw new Error('Content-Range not found');
  }

  const range = buildRangeFromRangerHeader(contentRange);
  if (range === undefined) {
    throw new Error('Invalid range while fetching employees');
  }

  return range;
}

function buildRangeFromRangerHeader(range: string) {
  const regex = /[ -/]/;
  const matches = range
    .split(regex)
    .map((match) => parseInt(match, 10))
    .filter((match) => !isNaN(match));

  return matches.length === 3 ? (matches as [number, number, number]) : undefined;
}

type ExcludeData = Exclude<RTKQueryDerivedBooleansType<unknown>, 'data'>;
export type RTKQueryErrorAndSuccess = { error?: FetchBaseQueryError | SerializedError } & Pick<
  ExcludeData,
  'isSuccess'
>;
type ArgType = ExcludeData & RTKQueryErrorAndSuccess;
export function aggregateRTKQueryBooleans(...flags: ArgType[]) {
  const result = { isLoading: false, isError: false, isSuccess: false, error: undefined } as Pick<
    ArgType,
    'isLoading' | 'isError' | 'isSuccess' | 'error'
  >;
  for (const flag of flags) {
    if (flag.isLoading) {
      result.isLoading = true;
    }

    if (flag.isSuccess) {
      result.isSuccess = true;
    }

    if (flag.isError && flag.error) {
      result.isError = true;
      result.error = flag.error;
    }
  }
  return result;
}

export function responseHasError(
  response:
    | {
        data: unknown;
      }
    | {
        error: FetchBaseQueryError | SerializedError;
      },
) {
  return Object.hasOwn(response, 'error');
}

export function transformUploadApiErrorResponse(response: FetchBaseQueryError) {
  const data = response.data as ApiErrorsShape;
  const traceId = data?.traceId;
  let transformed:
    | { ExceptionMessage: string; Context: string }[]
    | { exceptionMessage: string; context: string }[] = [];

  if (!data) {
    return transformed;
  }

  try {
    if (isLegacyApiError(data)) {
      if (data.Errors && data.Errors.length > 0) {
        transformed = data.Errors.map((error) => {
          return {
            context: error.Context,
            exceptionMessage: getErrorMessageWithTraceId(error.ExceptionMessage, traceId),
          };
        });
      }
    } else if (isEnhancedLegacyApiError(data)) {
      if (data.errors && data.errors.length > 0) {
        transformed = data.errors.map((error) => {
          return {
            context: error.Context,
            exceptionMessage: getErrorMessageWithTraceId(error.ExceptionMessage, traceId),
          };
        });
      }
    } else if (isModernApiErrors(data)) {
      if (data.errors && data.errors.length > 0) {
        transformed = data.errors.map((error) => {
          return {
            context: error.context,
            exceptionMessage: getErrorMessageWithTraceId(error.exceptionMessage, traceId),
          };
        });
      }
    }
  } catch (e) {
    return [];
  }

  return transformed;
}
